/**
 * The Command Center pillar tile and the pillar building blocks.
 *
 * `PillarTile` is the Carbon tile in mockup 01's pillar row (DESIGN.md "Pillar
 * tile (Command Center grid)"): the whole tile is one link that turns
 * layer-hover on hover; a swatch-led title; the 3.5rem light score and a
 * delta tag; one finding sentence in secondary text; a proportional
 * visualization pinned to the bottom with its helper caption; and a
 * link-colored "Open …" with an arrow. Tiles sit flush in a CondensedGrid.
 *
 * The older building blocks (`PillarShell`, `HeroMetric`, `MiniGrid`,
 * `ActionsBlock`, `Action`) keep their names and props for any importer,
 * restyled onto the Carbon primitives.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import {
  BigNumber,
  CondensedGrid,
  IconArrowDown,
  IconArrowRight,
  IconArrowUp,
  SeverityGlyph,
  Tag,
  Tile,
  TileHeader,
  TONE_CATEGORY,
  type Category,
  type SeverityLevel,
} from '@/components/carbon'
import ScoreDeltaChip from './ScoreDeltaChip'
import LearnMoreLink from './LearnMoreLink'
import type { ActionSeverity, PillarTone } from '@/components/carbon/types'

// Owned by the Carbon contracts since Wave 1; re-exported so existing importers keep working.
export type { ActionSeverity, PillarTone } from '@/components/carbon/types'

const LEVEL: Record<ActionSeverity, SeverityLevel> = {
  crit: 'crit',
  high: 'high',
  med: 'med',
  low: 'low',
  routine: 'low',
}

/* -------------------------------------------------------------------------- */
/* PillarTile                                                                 */
/* -------------------------------------------------------------------------- */

export interface PillarTileProps {
  /** The pillar's name ("Baseline Compliance"). */
  title: string
  /** The swatch leading the title. */
  category: Category
  /** The page the tile opens. */
  to: string
  /** The closing link text ("Open Baseline Compliance"). */
  linkLabel: string
  /** 0..100, or null for a dash. */
  score: number | null
  isLoading?: boolean
  /** The delta tag ("+7 in 90 days"). */
  delta?: ReactNode
  /** The one finding sentence. */
  finding?: ReactNode
  /** The proportional visualization with its caption, pinned to the bottom. */
  viz?: ReactNode
  /** The link's accessible name; defaults to "{title}, {score}". */
  ariaLabel?: string
  /** Extra body content (an empty state) between the finding and the visualization. */
  children?: ReactNode
}

export function PillarTile({
  title,
  category,
  to,
  linkLabel,
  score,
  isLoading = false,
  delta,
  finding,
  viz,
  ariaLabel,
  children,
}: PillarTileProps) {
  const shown = isLoading ? null : score
  const name = ariaLabel ?? (shown == null ? title : `${title}, ${Math.round(shown)}`)
  return (
    <Tile
      to={to}
      aria-label={name}
      aria-busy={isLoading || undefined}
      className="group flex min-w-0 flex-col c-md:min-h-[320px] c-xlg:min-h-[360px]"
    >
      <TileHeader title={title} category={category} level={3} />
      <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <BigNumber value={shown} size="figure-lg" as="span" />
        {isLoading ? null : delta}
      </div>
      {finding ? <p className="m-0 mt-3 text-body text-text-secondary [overflow-wrap:anywhere]">{finding}</p> : null}
      {children}
      <div aria-hidden="true" className="mt-auto pt-6">
        {viz}
      </div>
      <span className="mt-5 inline-flex items-center gap-2 text-body text-link group-hover:text-link-hover group-hover:underline">
        {linkLabel}
        <IconArrowRight />
      </span>
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Legacy building blocks, restyled                                           */
/* -------------------------------------------------------------------------- */

/** A pillar card with its own closing link. `roman` and `Icon` are accepted and no longer drawn (the No-Kicker Rule). */
export function PillarShell({
  tone,
  eyebrow,
  ctaLabel,
  ctaHref,
  children,
}: {
  tone: PillarTone
  roman: string
  eyebrow: string
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  ctaLabel: string
  ctaHref: string
  children: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col bg-layer-1 p-4">
      <TileHeader title={eyebrow} category={TONE_CATEGORY[tone]} level={3} />
      <div className="mt-5 flex flex-1 flex-col">{children}</div>
      <Link to={ctaHref} className="mt-5 inline-flex items-center gap-2 self-start text-body text-link hover:text-link-hover hover:underline">
        {ctaLabel}
        <IconArrowRight />
      </Link>
    </div>
  )
}

const TREND_ICON = { up: IconArrowUp, down: IconArrowDown, flat: null } as const

export function HeroMetric({
  value,
  unit,
  isLoading,
  trend,
}: {
  value: number | null | undefined
  unit: string
  isLoading: boolean
  trend: { dir: 'up' | 'down' | 'flat'; label: string } | null
}) {
  const Arrow = trend ? TREND_ICON[trend.dir] : null
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
      <BigNumber value={isLoading ? null : value} size="figure-lg" suffix={unit} as="span" />
      {trend ? (
        <Tag className="tabular-nums" icon={Arrow ? <Arrow /> : undefined}>
          {trend.label}
        </Tag>
      ) : null}
    </div>
  )
}

export function MiniGrid({
  cells,
}: {
  cells: { label: string; value: string; suffix?: string }[]
}) {
  return (
    <CondensedGrid columns={{ base: 2 }} className="mb-6">
      {cells.map(c => (
        <div key={c.label} className="bg-layer-1 px-4 py-3">
          <div className="text-helper text-text-helper">{c.label}</div>
          <div className="mt-1 text-body font-semibold tabular-nums text-text-primary">
            {c.value}
            {c.suffix && <span className="ml-1 text-helper font-normal text-text-helper">{c.suffix}</span>}
          </div>
        </div>
      ))}
    </CondensedGrid>
  )
}

export function ActionsBlock({
  label,
  allHref,
  ctaLabel = 'More items',
  children,
}: {
  label: string
  allHref: string
  /** CTA label for the link to the full list. Defaults to a generic advisor-tone phrase. */
  ctaLabel?: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-1">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="text-body font-semibold text-text-primary">{label}</span>
        <Link to={allHref} className="inline-flex items-center gap-2 text-body text-link hover:text-link-hover hover:underline">
          {ctaLabel}
          <IconArrowRight />
        </Link>
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  )
}

export function Action({
  severity,
  title,
  meta,
  guardianScoreDelta,
  href,
  learnMoreUrl,
}: {
  severity: ActionSeverity
  title: string
  meta: string
  /** Optional Guardian Score lift estimate. Renders an inline chip; null/undefined hides it. */
  guardianScoreDelta?: number | null
  /** Click target: the per-item `href` from `PriorityAction`, or a page-level fallback. */
  href: string
  /** Optional Microsoft Learn (or other doc) URL, opened in a new tab by its own control. */
  learnMoreUrl?: string | null
}) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 border-t border-line-subtle py-3 first:border-t-0">
      <SeverityGlyph level={LEVEL[severity]} size={12} />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link to={href} className="min-w-0 font-semibold text-text-primary [overflow-wrap:anywhere] hover:underline">
            {title}
          </Link>
          <ScoreDeltaChip delta={guardianScoreDelta} />
          <LearnMoreLink url={learnMoreUrl} size="sm" />
        </div>
        <div className="mt-0.5 text-helper text-text-helper [overflow-wrap:anywhere]">{meta}</div>
      </div>
    </div>
  )
}
